/**
 * Geometry and persistence for the draggable feedback pill.
 *
 * Split out of `feedback-widget.tsx` so the two rules that can strand the
 * pill — clamping against the viewport, and trusting whatever sits in
 * `localStorage` — are testable without mounting the widget (which pulls in
 * a server action and the intl provider). Nothing here touches `window`:
 * the caller passes the viewport and the storage it wants to read.
 */

export const POSITION_STORAGE_KEY = 'soku:feedback-widget-position'

export const DEFAULT_POSITION: WidgetPosition = { right: 16, bottom: 16 }

/**
 * Pointer travel (px) that turns a press into a drag. Below it the gesture
 * stays a click so the popover still opens on a slightly shaky press.
 */
export const DRAG_THRESHOLD = 4

/** Offsets from the viewport's right / bottom edges, in CSS pixels. */
export type WidgetPosition = { right: number; bottom: number }

export type Size = { width: number; height: number }

/**
 * Keep the pill fully inside the viewport.
 *
 * Offsets are measured from the right / bottom edges so the pill keeps its
 * relationship to the corner it lives in when the window is resized; the
 * clamp is re-applied on every resize so shrinking the window can never
 * leave a remembered position off-screen and unclickable.
 */
export function clampPosition(
  position: WidgetPosition,
  size: Size,
  viewport: Size,
): WidgetPosition {
  const maxRight = Math.max(0, viewport.width - size.width)
  const maxBottom = Math.max(0, viewport.height - size.height)
  return {
    right: Math.min(Math.max(position.right, 0), maxRight),
    bottom: Math.min(Math.max(position.bottom, 0), maxBottom),
  }
}

/**
 * Read a remembered position, or `null` when there is nothing trustworthy.
 *
 * Anything a user (or another tab, or a stale release) can put in the key is
 * hostile input: missing storage, unparseable JSON, a non-object, and
 * non-finite numbers all fall back to the default corner rather than
 * producing `NaN` offsets, which CSS drops and which would park the pill at
 * the viewport origin.
 */
export function readStoredPosition(
  storage: Pick<Storage, 'getItem'> | undefined,
): WidgetPosition | null {
  if (!storage) return null
  try {
    const raw = storage.getItem(POSITION_STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const { right, bottom } = parsed as Partial<WidgetPosition>
    if (!Number.isFinite(right) || !Number.isFinite(bottom)) return null
    return { right: right as number, bottom: bottom as number }
  } catch {
    // Unreadable / disabled storage just means the default corner.
    return null
  }
}
